import React, { useCallback, useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  isPatientAssessmentFormActivity, isPatientPsychiatryActivity, PatientActivity,
  PatientActivityType, PatientDocument, PatientUploadedMedicalDocument
} from '@uplift/api';
import { providerPatientRoute } from '@uplift/constants';
import { AssessmentKeys } from '@uplift/types';
import {
  AssessmentSubmissionsReviewModal, DocumentCenterModal, Loader, PatientChartReviewModal,
  ProviderLayout, UploadDocumentsModal
} from '@uplift/ui-v2';

import { ActivityList } from './components/Activity';
import { Assessments, CompactAssessments } from './components/Assessments';
import { ResponsiveLayout } from './components/Layouts';
import {
  AccordionsPatientDetails, GeneralPatientDetails
} from './components/PatientDetails/PatientDetails.component';
import { useFindInitialActivity } from './initial-activity.hook';

export type SelectedAssessment = {
  id: string;
  key: AssessmentKeys;
};

export const PatientDetailsPage: React.VFC = () => {
  const queryClient = useQueryClient();

  const params = providerPatientRoute.useParams();
  const { activityType, activityId } = providerPatientRoute.useAllQueryParams();

  const [ selectedActivityItem, setSelectedActivityItem ] = React.useState<PatientActivity>();
  const [ selectedAssessment, setSelectedAssessment ] = React.useState<SelectedAssessment | null>(null);
  const [ documentCenterOpened, setDocumentCenterOpened ] = React.useState(false);
  const [ selectedDocument, setSelectedDocument ] = React.useState<PatientDocument | null>(null);
  const [ uploadDocumentOpened, setUploadDocumentOpened ] = React.useState(false);
  const [ patientDetailsExpanded, setPatientDetailsExpanded ] = React.useState(false);

  const handleCloseCenter = () => setDocumentCenterOpened(false);
  const handleOpenCenter = () => setDocumentCenterOpened(true);

  const handleUploadDocumentSuccess = useCallback(async (doc: PatientUploadedMedicalDocument) => {
    await Promise.all([
      queryClient.invalidateQueries([ 'user', params.patientId, 'documents' ]),
      queryClient.invalidateQueries([ 'user', params.patientId, 'activity' ]),
    ]);

    setUploadDocumentOpened(false);
    setSelectedDocument(doc);
  }, [ params.patientId, queryClient ]);

  const handleSelectDocument = useCallback((document: PatientDocument) => {
    setSelectedDocument(document);
    setDocumentCenterOpened(true);
  }, []);

  const handleSelectActivity = useCallback((activity: PatientActivity) => {
    if (activity.activityType === PatientActivityType.document) {
      handleSelectDocument(activity.document);
    } else {
      setSelectedActivityItem(activity);
    }
  }, [ handleSelectDocument ]);

  const { findInitialActivity } = useFindInitialActivity(params.patientId, activityType, activityId);

  useEffect(() => {
    const find = async () => {
      const initialActivity = await findInitialActivity();

      if (initialActivity) {
        if (activityType === 'assessment' && isPatientAssessmentFormActivity(initialActivity)) {
          setSelectedAssessment({
            key: initialActivity.formKey,
            id: initialActivity.submission.formId
          });
        } else if (activityType === 'psychiatrySession' && isPatientPsychiatryActivity(initialActivity)) {
          setSelectedActivityItem(initialActivity);
        } else {
          setSelectedActivityItem(initialActivity);
        }
      }
    };

    find();
  }, [ activityType, findInitialActivity ]);

  return (
    <ProviderLayout bodyScroll={false}>
      <ResponsiveLayout
        patientDetailsExpanded={patientDetailsExpanded}
        setPatientDetailsExpanded={setPatientDetailsExpanded}
        patientDetails={(
          <GeneralPatientDetails
            patientId={params.patientId}
            expanded={patientDetailsExpanded}
            setExpanded={setPatientDetailsExpanded}
          />
        )}
        patientAccordions={(
          <AccordionsPatientDetails
            patientId={params.patientId}
            onDocumentSelect={handleSelectDocument}
            onViewAllDocuments={handleOpenCenter}
          />
        )}
        assessments={(
          <Assessments
            patientId={params.patientId}
            onAssessmentDetails={setSelectedAssessment}
          />
        )}
        compactAssessment={(
          <CompactAssessments patientId={params.patientId} />
        )}
        activities={(
          <React.Suspense fallback={<Loader color="secondary" />}>
            <ActivityList
              patientId={params.patientId}
              onUploadDocumentOpen={() => setUploadDocumentOpened(true)}
              onActivitySelect={handleSelectActivity}
            />
          </React.Suspense>
        )}
      />
      <DocumentCenterModal
        open={documentCenterOpened}
        onClose={handleCloseCenter}
        patientId={params.patientId}
        selectedDocument={selectedDocument}
        onSelectDocument={setSelectedDocument}
        onUploadDocument={() => setUploadDocumentOpened(true)}
      />
      <AssessmentSubmissionsReviewModal
        open={!!selectedAssessment}
        onClose={() => setSelectedAssessment(null)}
        patientId={params.patientId}
        assessmentId={selectedAssessment?.id}
        assessmentKey={selectedAssessment?.key}
      />
      <PatientChartReviewModal
        patientId={params.patientId}
        selectedItem={selectedActivityItem}
        onSelectItem={setSelectedActivityItem}
      />
      <UploadDocumentsModal
        patientId={params.patientId}
        open={uploadDocumentOpened}
        onClose={() => setUploadDocumentOpened(false)}
        onSuccess={handleUploadDocumentSuccess}
      />
    </ProviderLayout>
  );
};
